import { useState, useEffect, type ReactNode, useMemo, lazy, Suspense } from 'react';
import { ScopeBar } from './ScopeBar';
import { WorkQueue, type FilterMode, type ViewMode } from './WorkQueue';
import { DataReadinessBanner } from '../DataReadinessBanner';
import { useDecisionsQuery, useKpiHeadlineQuery, useKpiReadinessQuery } from '@/hooks/queries';
import {
  COMMAND_CENTER_FAST_DAYS_AHEAD,
  COMMAND_CENTER_FULL_DAYS_AHEAD,
} from '@/lib/commandCenterHorizon';

const OracleChat = lazy(() => import('../OracleChat').then((m) => ({ default: m.OracleChat })));

interface Property {
  id: string;
  name: string;
  readOnly?: boolean;
}

interface CommandCenterLayoutProps {
  property: string;
  properties: Property[];
  onPropertyChange: (id: string) => void;
  children: ReactNode;
  /** Hide the "all" portfolio option from property selector */
  hideAllOption?: boolean;
}

export function CommandCenterLayout({
  property,
  properties,
  onPropertyChange,
  children,
  hideAllOption = false,
}: CommandCenterLayoutProps) {
  if (import.meta.env.DEV) {
    console.log('[Render] <CommandCenterLayout>');
  }

  // Lightweight Render Counter for Debugging
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const renderWindow = window as Window & { __renders?: Record<string, number> };
    renderWindow.__renders = renderWindow.__renders ?? {};
    renderWindow.__renders.CommandCenterLayout =
      (renderWindow.__renders.CommandCenterLayout ?? 0) + 1;
  }

  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [oracleChatOpen, setOracleChatOpen] = useState(false);
  const [oracleChatMounted, setOracleChatMounted] = useState(false);
  const [isLoading] = useState(false);
  const [kpisEnabled, setKpisEnabled] = useState(false);

  // Calculate date range
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = today.toISOString().split('T')[0];
    const endDate = new Date(
      today.getTime() + COMMAND_CENTER_FULL_DAYS_AHEAD * 24 * 60 * 60 * 1000,
    );
    const end = endDate.toISOString().split('T')[0];
    setDateRange({ start, end });
  }, []);

  // PERF: Defer KPI/readiness fetch until after the first paint.
  // This keeps the initial dashboard shell (ScopeBar + WorkQueue + main) fast.
  useEffect(() => {
    const w = window as Window & {
      __initialLoadTraceMark?: (name: string) => void;
      requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (import.meta.env.DEV && typeof w.__initialLoadTraceMark === 'function') {
      w.__initialLoadTraceMark('dashboard_shell_mounted');
    }

    const enable = () => setKpisEnabled(true);
    // requestIdleCallback is best-effort; fall back to a short timeout.
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(enable, { timeout: 1500 });
      return () => w.cancelIdleCallback?.(id);
    }
    // Use globalThis to avoid TS narrowing oddities around `window` in strict mode.
    const t = globalThis.setTimeout(enable, 300);
    return () => globalThis.clearTimeout(t);
  }, []);

  const kpiHeadlineQuery = useKpiHeadlineQuery(property, { enabled: kpisEnabled });
  const readinessQuery = useKpiReadinessQuery(property, {
    enabled: kpisEnabled && (kpiHeadlineQuery.isSuccess || kpiHeadlineQuery.isError),
  });
  const kpis = useMemo(
    () => ({
      occupancy: kpiHeadlineQuery.data?.kpis.occupancy ?? 0,
      adr: kpiHeadlineQuery.data?.kpis.adr ?? 0,
    }),
    [kpiHeadlineQuery.data?.kpis.occupancy, kpiHeadlineQuery.data?.kpis.adr],
  );
  const readinessSummary = readinessQuery.data?.readinessSummary ?? null;
  const readinessPerProperty = readinessQuery.data?.readinessPerProperty ?? null;
  const readinessAsOf = readinessQuery.data?.readinessAsOf ?? null;
  const decisionCountsQuery = useDecisionsQuery(property, COMMAND_CENTER_FAST_DAYS_AHEAD, {
    enabled: kpisEnabled && !!property && property !== 'all',
    lite: true,
    gridOnly: true,
    includeCounts: true,
    countsOnly: true,
  });

  const currentProperty = useMemo(
    () => properties.find((p) => p.id === property) || { id: property, name: property, units: 0 },
    [properties, property],
  );

  const propertiesWithUnits = useMemo(
    () => properties.map((p) => ({ ...p, units: 0 })),
    [properties],
  );

  const scopeBarProperty = useMemo(
    () => ({
      ...currentProperty,
      units: 0,
    }),
    [currentProperty],
  );

  const counts = useMemo(
    () =>
      decisionCountsQuery.data?.counts ?? {
        pending: { total: 0, inWindow: 0 },
        approved: { total: 0, inWindow: 0 },
        rejected: { total: 0, inWindow: 0 },
      },
    [decisionCountsQuery.data?.counts],
  );

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <a
        href="#command-center-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-sm focus:bg-[var(--surface-raised)] focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
      >
        Skip to main content
      </a>
      <ScopeBar
        property={scopeBarProperty}
        properties={propertiesWithUnits}
        onPropertyChange={onPropertyChange}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        kpis={kpis}
        isLoading={isLoading}
        onAskSilas={() => {
          setOracleChatMounted(true);
          setOracleChatOpen(true);
        }}
        hideAllOption={hideAllOption}
        showRateControls={false}
      />

      <DataReadinessBanner
        summary={readinessSummary}
        perProperty={readinessPerProperty}
        asOf={readinessAsOf}
        propertyLabels={Object.fromEntries(properties.map((p) => [p.id, p.name]))}
      />

      <div className="flex-1 flex min-h-0 flex-col md:flex-row overflow-hidden">
        <WorkQueue
          filterMode={filterMode}
          onFilterChange={setFilterMode}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          counts={counts}
        />

        <main
          id="command-center-main"
          tabIndex={-1}
          className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-[var(--bg)] focus:outline-none"
        >
          {children}
        </main>
      </div>

      {oracleChatMounted && (
        <Suspense fallback={null}>
          <OracleChat
            isOpen={oracleChatOpen}
            onClose={() => setOracleChatOpen(false)}
            property={property}
            propertyName={currentProperty?.name}
          />
        </Suspense>
      )}
    </div>
  );
}
